// Auth track (track/auth): the logged-in-user session, real authentication
// now (a bearer token from PIN login), still tab-scoped in sessionStorage,
// deliberately unchanged from the pre-auth stub's own security posture
// (pin-policy-proposal.md's rejected-alternatives section: "a shared
// terminal that stays logged in across a full browser restart is a shared
// terminal anyone who reboots or reopens it inherits"). `employeeId`
// carries the logged-in user's OWN display name (Amendment 2026-08-22
// third: "the prepared-by attribution uses the logged-in user
// automatically"), so every existing call site in ui/src/main.ts that
// already reads session.employeeId for maker attribution, the session
// badge, tips-by-maker, etc. keeps working unchanged; nothing about that
// field's SHAPE changed, only how it gets populated (PIN login instead of
// a free-text box).
//
// See ui/src/main.ts's own "Login / logout" section for the full flow.
// See terminalConfig below for the SEPARATE, device-scoped (localStorage)
// "which server and tenant does this terminal talk to" value that
// deliberately survives a logout, unlike this session.

// UX22/DEC-137 slice 2: money.ts carries zero imports of its own
// (confirmed this session: `grep -n "^import" ui/src/money.ts` returns
// nothing), the same dependency-free shape this file already has, so
// importing its two display formatters here for
// percentBoundRangeText/maxDollarsBoundText below introduces no cycle --
// admin.ts and main.ts both already import from money.ts directly, and
// nothing in money.ts imports session.ts.
import { basisPointsToPercentString, formatCents } from "./money.ts";

// Independent-review Finding B, 2026-08-26: mirrors ui/src/api.ts's own
// PermissionSnapshot/PermissionGrant (which in turn mirror src/domain/
// permissions.ts's Grant type exactly) -- see that module's own
// resolveEffectivePermissionSnapshot header for the full reasoning. Not
// re-exported from api.ts to avoid a circular import (api.ts imports
// nothing from this file); the shape is identical by construction and
// isSession below validates it structurally.
export type PermissionGrant = "allowed" | "approval_required" | "denied";
export type PermissionSnapshot = Record<string, PermissionGrant>;

export interface Session {
  apiBaseUrl: string;
  tenantId: string;
  /** The logged-in user's own display name. See this module's header. */
  employeeId: string;
  /** Bearer token from POST .../auth/login. Never logged, never shown;
   * see scripts/check-no-pin-in-logs.ts. */
  token: string;
  userId: string;
  role: string;
  /** Independent-review Finding B, 2026-08-26: the user's FULL role set
   * (src/domain/permissions.ts's user_roles), not just the single legacy
   * `role` above. Populated from POST .../auth/login and GET .../auth/
   * session's own `user.roles` (src/api/server.ts). Kept alongside
   * `permissions` below rather than instead of it: `permissions` is what
   * every UI visibility decision must actually read (see
   * hasAnyPermission below, and CLAUDE.md's permission-driven rule),
   * `roles` exists ONLY for showing role membership as text (the account
   * badge, My Profile), never for deciding anything. */
  roles: string[];
  /** Independent-review Finding B, 2026-08-26: this user's resolved
   * effective permission for every tenant-scoped catalog permission,
   * computed server-side by resolveEffectivePermissionSnapshot (the SAME
   * function-and-algorithm requirePermission uses on every route,
   * batched) and handed to the client rather than making it re-implement
   * the override/role-union resolution algorithm. This is the ONLY thing
   * any UI gate reads: see hasPermission/hasAnyPermission below.
   * Presentation only -- see that function's own comment for why this can
   * never become a second authorization boundary. */
  permissions: PermissionSnapshot;
  mustChangePin: boolean;
  defaultThemeId: string | null;
  /** T16/DEC-133: this tenant's resolved PIN-length policy (src/domain/
   * tenantSettings.ts's PinPolicy), populated from the login and
   * session-refresh responses the same way sessionInactivityMinutes
   * already is. The shared PIN pad (main.ts) and the admin-only Reset PIN
   * / Add User sheets (admin.ts) read these instead of a hardcoded 4-6
   * range once a session exists; see main.ts's own PIN-pad section header
   * for the one case (the pre-login pad) that cannot read this and uses a
   * fixed global bound instead. */
  pinMinLength: number;
  pinMaxLength: number;
}

/** T16/DEC-133: one home for the "4 digits" vs "4 to 6 digits" wording
 * rule, so the client never describes a configured PIN-length range a
 * different way than the server would on a rejection
 * (src/domain/users.ts's assertValidChosenPin uses the identical rule).
 * Shared by main.ts's forced-change subtitle and admin.ts's Reset PIN /
 * Add User error messages rather than re-implemented in each. */
export function pinLengthRangeText(minLength: number, maxLength: number): string {
  return minLength === maxLength ? `${minLength} digits` : `${minLength} to ${maxLength} digits`;
}

/** UX22/DEC-137: the sibling of pinLengthRangeText above, for a STATIC
 * engineering bound (a convention's own literal, imported from
 * ../../scripts/settingsBounds.ts) rather than a LIVE, per-tenant policy
 * value. pinLengthRangeText formats what a tenant has actually configured
 * (fetched at login/session-refresh); this formats the fixed ceiling and
 * floor a settings field's own description states above the field, before
 * anyone decides what to type. Composes "4 to 12 digits" or, when a bound
 * collapses to one number, "4 digits", never a hand-typed sentence, so a
 * field's description and its validator cannot silently disagree once
 * both import the same constant. */
export function boundRangeText(min: number, max: number, unit: string): string {
  return min === max ? `${min} ${unit}` : `${min} to ${max} ${unit}`;
}

/** UX22/DEC-137 slice 2: the percent-rate sibling of boundRangeText above.
 * Every rate bound in tenant_settings (tax rate, tip presets, hold
 * increment threshold, abandoned tab gratuity) is stored and validated in
 * integer basis points, but a person types and reads a percentage, never
 * a basis-point count -- 10000 means nothing to someone deciding what to
 * type; 100% does. Converts with basisPointsToPercentString, the exact,
 * lossless conversion ui/src/money.ts already uses for every OTHER
 * percent value on this same settings screen (the tip-preset chips,
 * Discounts' percent-off field), so a bound description cannot render a
 * number in a different style than the value itself round-trips
 * elsewhere on the same screen. Percent appears on both sides of the
 * range ("0.00% to 100.00%"), not once at the end, since a bare leading
 * number before "to" would otherwise read as an unlabeled unit. */
export function percentBoundRangeText(minBasisPoints: number, maxBasisPoints: number): string {
  const min = basisPointsToPercentString(minBasisPoints);
  const max = basisPointsToPercentString(maxBasisPoints);
  return min === max ? `${min}%` : `${min}% to ${max}%`;
}

/** UX22/DEC-137 slice 2: the ceiling-only dollar-amount sibling of
 * boundRangeText above. A hold amount's own bound is stored and validated
 * in integer cents (MAX_HOLD_AMOUNT_CENTS), but a person types and reads
 * dollars and cents, never a raw cent count. Converts with formatCents,
 * the exact conversion every other dollar amount on this screen already
 * uses (a bottle's sell price, a shelf tier's price), so this cannot
 * render a dollar figure in a different style than the rest of the admin
 * surface. Ceiling only, not a range: the field's own floor is a
 * structural "must be positive" (zero is not itself a chosen engineering
 * bound the way the ceiling is), stated as fixed prose at the one call
 * site that uses this rather than composed here. */
export function maxDollarsBoundText(maxCents: number): string {
  return formatCents(maxCents);
}

/** UX22/DEC-137 slice 4: the FORMAT sibling of boundRangeText above, for
 * a bound expressed as an allowed set of URL schemes rather than a
 * magnitude. src/domain/vendors.ts's validateOrderWebsiteUrl accepts
 * exactly the schemes in src/domain/settingsBounds.ts's
 * ORDER_WEBSITE_URL_SCHEMES (each already carrying its own trailing
 * colon, e.g. "http:"); this composes "http:// or https://" from that
 * same array, never a hand-typed copy of the two scheme names. */
export function urlSchemeBoundText(schemes: readonly string[]): string {
  return schemes.map((scheme) => `${scheme}//`).join(" or ");
}

/** UX22/DEC-137 slices 4-5: the character-count sibling of
 * maxDollarsBoundText above, for a free-typed text field whose only
 * engineering bound is a ceiling on length (the employee profile fields,
 * shelf tier name). No minimum beyond "must not be blank," which is a
 * presence rule stated as fixed prose at the call site, not a magnitude
 * this composes. */
export function maxLengthBoundText(maxChars: number): string {
  return `${maxChars} characters`;
}

/** Independent-review Finding B, 2026-08-26. The one place a permission
 * snapshot turns into a plain "show or hide" decision: true unless the
 * resolved grant is exactly "denied" (an "approval_required" permission
 * still means the user has SOME real access to that surface -- the
 * approval gate is enforced at the moment of the action itself, by the
 * same requirePermission call every route already makes, not by hiding
 * the entry point). A permission id this snapshot has no entry for
 * (never expected once login/session always resolves the full tenant-
 * scoped catalog, but defensive rather than assumed) is treated as
 * denied, the same fail-closed default resolveEffectivePermission itself
 * uses for a role missing its own row.
 *
 * This function, and everything that calls it, decides ONLY what the UI
 * shows. It is never consulted by anything that sends a request with
 * side effects; every one of those still goes through the server's own
 * requirePermission on every call, unweakened and unbypassable from
 * here, exactly as before this finding. See main.ts's admin-tab-
 * visibility wiring (the actual call sites) for where this is used. */
export function hasPermission(session: Session, permissionId: string): boolean {
  // Gate re-review correction, 2026-08-26: this used to read
  // `session.permissions[permissionId] !== "denied"` directly, which
  // does NOT fail closed for a permission id absent from the snapshot --
  // `undefined !== "denied"` is true, so a missing entry silently
  // GRANTED the surface it gates, the opposite of this function's own
  // doc comment above ("treated as denied, the same fail-closed default
  // resolveEffectivePermission itself uses"). Reading the grant into a
  // local first and checking it is defined makes the undefined case its
  // own explicit branch instead of an accidental side effect of `!==`.
  const grant = session.permissions[permissionId];
  return grant !== undefined && grant !== "denied";
}

/** True if the session holds ANY of the listed permissions -- the "show
 * this entry point at all" question, for a button (like the topbar
 * Settings item) that fans out to several finer-grained surfaces (like
 * the admin screen's own tabs), each independently gated by
 * hasPermission at the point it actually renders. */
export function hasAnyPermission(session: Session, permissionIds: readonly string[]): boolean {
  return permissionIds.some((id) => hasPermission(session, id));
}

// Product-owner ruling 2026-08-28 ("Nothing EVER should be role driven
// from that I can see. All actions should be permission driven"), now a
// non-negotiable product rule in CLAUDE.md and enforced by
// scripts/check-no-role-driven-gating.ts. isManagerTierRole used to live
// here: `role === "admin" || role === "manager"`, the single expression
// three separate UI decisions were keyed to. It is DELETED rather than
// narrowed, because role templates are becoming fully configurable
// (backlog R3 to R7, DEC-024) and an Admin will be able to create roles
// this build has never heard of and change what any existing role may
// do. Every one of those three decisions now resolves the PERMISSION the
// server itself checks:
//
//   - the payment-terminal picker's account-menu button, and the picker
//     screen's own gate, read floor.terminal.select (TERM1; the exact
//     permission GET .../terminals/selectable is gated on) via
//     hasPermission above;
//   - the approver picker asks the server which users can approve the
//     specific permission at stake (GET .../approval-candidates), rather
//     than guessing from a role tier that was never the same set.
//
// Session.role and Session.roles survive as DATA (the account badge, My
// Profile, a display label). Nothing decides anything from them.

const ROLE_DISPLAY_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  assistant_manager: "Assistant Lounge Manager",
  lounger: "Lounger",
  inventory_manager: "Inventory Manager",
  report_creator: "Report Creator",
};

/** Plain-language label for a single role id, for the tile picker's own
 * badge and anywhere else a user's PRIMARY role is shown as text. Falls
 * back to the raw id for forward-compatibility with a role this build
 * does not yet know about, rather than showing nothing. */
export function roleDisplayLabel(role: string): string {
  return ROLE_DISPLAY_LABELS[role] ?? role;
}

const SESSION_STORAGE_KEY = "bms-ui-session";

function isSession(value: unknown): value is Session {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record["apiBaseUrl"] === "string" &&
    typeof record["tenantId"] === "string" &&
    typeof record["employeeId"] === "string" &&
    typeof record["token"] === "string" &&
    typeof record["userId"] === "string" &&
    typeof record["role"] === "string" &&
    // Independent-review Finding B, 2026-08-26: a stored session from
    // before this fix (a browser tab that has not reloaded since) has
    // neither field and is now correctly rejected here -- loadSession
    // returns undefined, the same "fall back to the user picker" path an
    // expired/unknown token already takes, rather than silently running
    // with an incomplete session that would resolve every hasPermission
    // check to denied.
    Array.isArray(record["roles"]) &&
    record["roles"].every((r) => typeof r === "string") &&
    typeof record["permissions"] === "object" &&
    record["permissions"] !== null &&
    typeof record["mustChangePin"] === "boolean" &&
    (record["defaultThemeId"] === null || typeof record["defaultThemeId"] === "string") &&
    // T16/DEC-133: a stored session from before this fix (a browser tab
    // that has not reloaded since) has neither field and is now correctly
    // rejected here, the same "fall back to the user picker" precedent
    // Finding B's own comment above already established for roles/
    // permissions -- a session missing its PIN policy must not run the
    // shared pad with an unknown accepted length.
    typeof record["pinMinLength"] === "number" &&
    typeof record["pinMaxLength"] === "number"
  );
}

export function loadSession(): Session | undefined {
  const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (raw === null) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isSession(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function saveSession(session: Session): void {
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

// ---------------------------------------------------------------------
// Terminal config: which server (apiBaseUrl) and tenant this PHYSICAL
// terminal talks to, and (SEC2-S3) the device credential that proves which
// location it is. Set once by redeeming a manager's setup code on the setup
// screen (ui/src/terminalSetup.ts), then persisted in localStorage so it
// survives every later login and logout. A logout clears the Session above
// but leaves this alone, which is what makes "log out returns to the [user]
// picker" (not back to setup) possible.
//
// SEC2-S3: this record now holds a CREDENTIAL, the tablet's own device secret
// (ui/src/deviceCredential.ts), sent as X-Device-Token at sign-in. It was
// "where do I ask" and nothing more before; that comment is gone because it
// is no longer true (brief section 12). The secret is never logged, never put
// in a URL and never shown, and the server keeps only a hash. Everything else
// added here (names of the business, location and tablet) is display-only: the
// server re-derives tenant and location from the credential on every request.
// SEC2-S4 (DEC-320): the credential is REQUIRED. A config without one (saved
// before SEC2-S3) is "not set up": the tablet shows the setup screen and a
// manager's code ties it to its location, once. There is no grace period.
// ---------------------------------------------------------------------

export interface TerminalConfig {
  apiBaseUrl: string;
  tenantId: string;
  /** Terminal-assignment slice (docs/solution-architecture.md Section
   * 8.3, realized). The registered payment terminal (src/domain/
   * terminals.ts's payment_terminals) THIS physical device IS, chosen
   * on demand from Open Tabs' own account-menu "Payment Terminal" item
   * (a manager-gated picker screen, since choosing or registering a
   * terminal reuses the same manager-only routes Settings > Payments >
   * Terminals already gates) and sent with every payment-taking request
   * from here on (ui/src/api.ts's payOrder/collectShare/openTabHold all
   * read terminalConfigState.paymentTerminalId). Deliberately optional
   * and separate from apiBaseUrl/tenantId above: a device already
   * connected to a server/tenant has no payment terminal picked yet
   * until a manager visits that menu item, and the server's own
   * resolveTerminalReferenceForCharge accepts an undefined terminalId
   * from the simulated provider in the meantime (its own documented
   * fallback), so there is no correctness reason to force this choice
   * before the device can otherwise be used. Survives logout, the same
   * reason apiBaseUrl/tenantId do (this module's own header): it names
   * hardware, not a credential. */
  paymentTerminalId?: string;
  /** Display-only convenience so the picker's own "already set up"
   * state, and the ticket rail's own small print, never need a round
   * trip to look up a name for an id already in hand. Never used for
   * authorization or resolution: every server-side charge re-resolves
   * paymentTerminalId to its real provider_reference itself
   * (src/domain/payments.ts's resolveTerminalReferenceForCharge), tenant-
   * scoped and active-only, so a stale name here is cosmetic only, never
   * a trust boundary. */
  paymentTerminalDisplayName?: string;
  /** SEC2-S3/S4: the tablet's own device secret (43 base64url characters). The one credential in this record, and
   * required: the server refuses the picker and sign-in without it. */
  deviceToken: string;
  /** SEC2-S3, display only (the server re-derives all of these from the credential): */
  deviceId?: string;
  locationId?: string;
  /** The location this tablet is tied to, as the redemption named it. */
  locationName?: string;
  /** The tablet's own name, as the manager chose it. */
  deviceName?: string;
  /** The business, as the redemption named it. */
  businessName?: string;
}

const TERMINAL_CONFIG_STORAGE_KEY = "bms-ui-terminal-config";

const DEVICE_TOKEN_FORMAT = /^[A-Za-z0-9_-]{43}$/;

function isTerminalConfig(value: unknown): value is TerminalConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record["apiBaseUrl"] === "string" &&
    typeof record["tenantId"] === "string" &&
    (record["paymentTerminalId"] === undefined ||
      typeof record["paymentTerminalId"] === "string") &&
    (record["paymentTerminalDisplayName"] === undefined ||
      typeof record["paymentTerminalDisplayName"] === "string") &&
    // SEC2-S3/S4: a missing or malformed credential is "not set up", never trusted and never sent.
    typeof record["deviceToken"] === "string" &&
    DEVICE_TOKEN_FORMAT.test(record["deviceToken"]) &&
    ["deviceId", "locationId", "locationName", "deviceName", "businessName"].every(
      (key) => record[key] === undefined || typeof record[key] === "string",
    )
  );
}

// TEN11-F1: a support tab (a software owner's Open as support) holds its server and tenant in memory only. The
// tablet configuration in localStorage belongs to the staff on this device: a support tab neither reads nor
// writes it, and has no device credential, so the in-memory config carries an empty one that is never sent.
let supportTerminalConfig: TerminalConfig | undefined;

/** Puts this tab in support mode: from here the terminal config is the one given, held in memory, and the
 * localStorage config is not touched by load, save or clear. */
export function enterSupportTerminalConfig(config: {
  apiBaseUrl: string;
  tenantId: string;
  locationId: string;
  locationName: string;
  businessName: string;
}): TerminalConfig {
  supportTerminalConfig = {
    apiBaseUrl: config.apiBaseUrl.replace(/\/$/, ""),
    tenantId: config.tenantId,
    deviceToken: "",
    locationId: config.locationId,
    locationName: config.locationName,
    businessName: config.businessName,
  };
  return supportTerminalConfig;
}

export function isSupportTerminalConfig(): boolean {
  return supportTerminalConfig !== undefined;
}

export function loadTerminalConfig(
  storage: KeyValueStorage = window.localStorage,
): TerminalConfig | undefined {
  if (supportTerminalConfig !== undefined) return supportTerminalConfig;
  const raw = storage.getItem(TERMINAL_CONFIG_STORAGE_KEY);
  if (raw === null) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isTerminalConfig(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function saveTerminalConfig(
  config: TerminalConfig,
  storage: KeyValueStorage = window.localStorage,
): void {
  if (supportTerminalConfig !== undefined) {
    supportTerminalConfig = config;
    return;
  }
  storage.setItem(TERMINAL_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

/** Clears the stored server, tenant and device credential, returning to the setup step. SEC2-S3: no button on
 * the picker calls this any more (a bartender must not be able to unbind the location's tablet mid-service); it
 * runs when the server says this tablet's credential is no longer recognised (a manager revoked it). Does NOT
 * clear the user Session; callers pair this with clearSession() when they mean "start completely over." */
export function clearTerminalConfig(storage: KeyValueStorage = window.localStorage): void {
  if (supportTerminalConfig !== undefined) return;
  storage.removeItem(TERMINAL_CONFIG_STORAGE_KEY);
}

/** What the redeem route answers (src/api/server.ts POST /device-setup/redeem). */
export interface RedeemedDevice {
  deviceId: string;
  tenantId: string;
  locationId: string;
  locationName: string;
  displayName: string;
  businessName: string;
}

/** The terminal config a successful redemption produces. The payment terminal choice survives only a rebind to
 * the same business AND the same location (TERM4): a reader registered to another business, or to another location of this
 * one, means nothing here. A previous config that never recorded its location is not trusted to be at this one. */
export function terminalConfigFromRedemption(
  apiBaseUrl: string,
  redeemed: RedeemedDevice,
  deviceSecret: string,
  previous: TerminalConfig | undefined,
): TerminalConfig {
  const keepPaymentTerminal =
    previous !== undefined &&
    previous.tenantId === redeemed.tenantId &&
    previous.locationId === redeemed.locationId &&
    previous.paymentTerminalId !== undefined;
  return {
    apiBaseUrl: apiBaseUrl.replace(/\/$/, ""),
    tenantId: redeemed.tenantId,
    deviceToken: deviceSecret,
    deviceId: redeemed.deviceId,
    locationId: redeemed.locationId,
    locationName: redeemed.locationName,
    deviceName: redeemed.displayName,
    businessName: redeemed.businessName,
    ...(keepPaymentTerminal && previous?.paymentTerminalId !== undefined
      ? { paymentTerminalId: previous.paymentTerminalId }
      : {}),
    ...(keepPaymentTerminal && previous?.paymentTerminalDisplayName !== undefined
      ? { paymentTerminalDisplayName: previous.paymentTerminalDisplayName }
      : {}),
  };
}

/** The picker's plain line: which tablet this is and which location it belongs to. Absent for a config with no
 * device (set up before SEC2-S3). */
export function deviceLineFor(config: TerminalConfig): { name: string; locationName: string } | undefined {
  if (config.deviceName === undefined || config.locationName === undefined) {
    return undefined;
  }
  return { name: config.deviceName, locationName: config.locationName };
}

/** Terminal-assignment slice: records the payment terminal a manager just
 * picked (or registered) at the terminal-picker step, persisting it
 * alongside the existing apiBaseUrl/tenantId in the SAME localStorage
 * entry. Callers pass the full CURRENT config (ui/src/main.ts's own
 * terminalConfigState) so apiBaseUrl/tenantId are never dropped by this
 * narrower write. */
export function savePaymentTerminalSelection(
  config: TerminalConfig,
  paymentTerminalId: string,
  paymentTerminalDisplayName: string,
): TerminalConfig {
  const updated: TerminalConfig = { ...config, paymentTerminalId, paymentTerminalDisplayName };
  saveTerminalConfig(updated);
  return updated;
}

// ---------------------------------------------------------------------
// PAY7 (docs/build-briefs/PAY7-pay-retry-after-terminal-change.md
// Section 2.8): one unresolved pay idempotency key per (tenantId, target),
// NEVER a credential and never guest/client data (see
// check:no-client-pii-in-logs's own named identifiers -- a target is
// "whole:<orderId>" / "guest:<orderId>:<guestId>" / "share:<shareId>",
// none of them free text), so sessionStorage (survives a reload, not a
// closed tab) rather than localStorage (survives logout too) is the right
// lifetime: broad enough to survive the ONE thing that actually defeats
// today's in-memory-only map (a reload), narrow enough not to accumulate
// forever across days. Deliberately keyed by tenantId, not by the
// signed-in user: the safety property this protects (never charge the
// SAME underlying provider intent twice) is a property of the TICKET, not
// of who is currently driving the tablet, so a different employee signing
// in on the SAME tablet after a sign-out must still see and reuse the
// SAME pending key for the SAME ticket -- clearing it on sign-out would
// defeat this exactly the way NOT persisting it defeats a reload.
// Sign-out (clearSession) therefore does NOT touch this storage,
// deliberately, the same way it already does not touch TerminalConfig.
// ---------------------------------------------------------------------

const PAY_IDEMPOTENCY_KEYS_STORAGE_KEY = "bms-ui-pay-idempotency-keys";

/** Minimal storage shape this module's own three functions below actually
 * use, so a plain node:test file can inject a trivial Map-backed fake
 * (no `window` or `sessionStorage` exists in that runtime) rather than
 * needing jsdom or an equivalent dependency -- the same injection pattern
 * ui/src/payIdempotencyKeys.ts's own constructor already establishes for
 * mintKey/signal. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function storageKey(tenantId: string, target: string): string {
  return `${tenantId}:${target}`;
}

function readPersistedPayKeyMap(storage: KeyValueStorage): Record<string, string> {
  try {
    const raw = storage.getItem(PAY_IDEMPOTENCY_KEYS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const record = parsed as Record<string, unknown>;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(record)) {
      if (typeof v === "string") result[k] = v;
    }
    return result;
  } catch {
    // Storage unavailable (private-mode quota, a disabled API) or the
    // stored value is corrupt. Either way: behave exactly as this
    // codebase does today, in memory only, for THIS session. Logged once
    // so a diagnostic session can see it happened, never logging the
    // stored value itself (it may carry live idempotency keys).
    console.warn(
      "pay idempotency keys: sessionStorage is unavailable or corrupt; falling back to " +
        "in-memory-only tracking for this page load (a reload will mint fresh keys)",
    );
    return {};
  }
}

/** Every pending key persisted for `tenantId`, keyed by TARGET (the
 * tenant prefix stripped), ready to feed straight into
 * PayIdempotencyKeyMap.restore. */
export function loadPersistedPayKeys(
  tenantId: string,
  storage: KeyValueStorage = window.sessionStorage,
): Record<string, string> {
  const all = readPersistedPayKeyMap(storage);
  const prefix = `${tenantId}:`;
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(all)) {
    if (k.startsWith(prefix)) result[k.slice(prefix.length)] = v;
  }
  return result;
}

/** Persists `target`'s own pending key for `tenantId`, surviving a reload.
 * Called every time a pay idempotency key is minted or reused (main.ts's
 * getOrMintPayIdempotencyKey), so the durable copy is written before the
 * network request that might fail ambiguously even starts. */
export function savePersistedPayKey(
  tenantId: string,
  target: string,
  key: string,
  storage: KeyValueStorage = window.sessionStorage,
): void {
  try {
    const all = readPersistedPayKeyMap(storage);
    all[storageKey(tenantId, target)] = key;
    storage.setItem(PAY_IDEMPOTENCY_KEYS_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Same fallback as above: this attempt silently does not persist; the
    // in-memory map (unchanged, Section 1's own existing behavior) still
    // carries the key for the REST of this page load, which is exactly
    // today's status quo, not a regression.
  }
}

/** Removes `target`'s own persisted key for `tenantId`. Never touches any
 * OTHER target's entry -- the persisted copy of the same per-target
 * contract clearPayIdempotencyKey already establishes for the in-memory
 * map. */
export function clearPersistedPayKey(
  tenantId: string,
  target: string,
  storage: KeyValueStorage = window.sessionStorage,
): void {
  try {
    const all = readPersistedPayKeyMap(storage);
    delete all[storageKey(tenantId, target)];
    storage.setItem(PAY_IDEMPOTENCY_KEYS_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // If we could not persist it in the first place, there is nothing
    // durable left to clear; a no-op here is consistent, not a gap.
  }
}

// ---------------------------------------------------------------------
// PAY13 (docs/build-briefs/PAY13-adopt-orphaned-hold.md Section 2.2),
// DEC-200: one unresolved hold idempotency key per (tenantId, orderId),
// mirroring PAY_IDEMPOTENCY_KEYS_STORAGE_KEY above exactly (see that
// item's own comment for the sessionStorage-not-localStorage reasoning,
// which applies identically here: survive a reload, not a closed tab;
// keyed by tenant, not by the signed-in user, so a different employee on
// the same tablet sees and reuses the SAME pending key for the SAME tab).
// A hold has no guest/share split to key on (DEC-002 already refuses a
// second hold while one is active, HoldAlreadyExistsError, unaffected by
// this item), so this is narrower than the Pay map above: one pending key
// at a time, keyed by orderId rather than by the Pay flow's own richer
// target string.
// ---------------------------------------------------------------------

const HOLD_IDEMPOTENCY_KEYS_STORAGE_KEY = "bms-ui-hold-idempotency-keys";

function readPersistedHoldKeyMap(storage: KeyValueStorage): Record<string, string> {
  try {
    const raw = storage.getItem(HOLD_IDEMPOTENCY_KEYS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    const record = parsed as Record<string, unknown>;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(record)) {
      if (typeof v === "string") result[k] = v;
    }
    return result;
  } catch {
    // Same fallback PAY7's own readPersistedPayKeyMap uses: storage
    // unavailable or corrupt, behave in-memory-only for this page load.
    console.warn(
      "hold idempotency keys: sessionStorage is unavailable or corrupt; falling back to " +
        "in-memory-only tracking for this page load (a reload will mint fresh keys)",
    );
    return {};
  }
}

/** The pending hold key for `(tenantId, orderId)`, or `undefined` when none
 * is pending. */
export function loadPersistedHoldKey(
  tenantId: string,
  orderId: string,
  storage: KeyValueStorage = window.sessionStorage,
): string | undefined {
  const all = readPersistedHoldKeyMap(storage);
  return all[storageKey(tenantId, orderId)];
}

/** Persists this order's own pending hold key for `tenantId`, surviving a
 * reload. Called every time a hold idempotency key is minted or reused
 * (main.ts's performOpenHold), so the durable copy is written before the
 * network request that might fail ambiguously even starts. */
export function savePersistedHoldKey(
  tenantId: string,
  orderId: string,
  key: string,
  storage: KeyValueStorage = window.sessionStorage,
): void {
  try {
    const all = readPersistedHoldKeyMap(storage);
    all[storageKey(tenantId, orderId)] = key;
    storage.setItem(HOLD_IDEMPOTENCY_KEYS_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // PAY13 Section 2c/F2 (docs/build-briefs/PAY13-adopt-orphaned-hold.md):
    // this attempt silently does not persist. Before F2, the comment here
    // claimed "the caller's own in-memory value still carries the key for
    // the REST of this page load" -- that claim was FALSE for the hold
    // flow specifically: main.ts's own performOpenHold called
    // loadPersistedHoldKey fresh from storage on every single tap, with
    // no separate in-memory value anywhere to fall back to, so a throwing
    // sessionStorage meant a BRAND NEW key was minted on every retry, for
    // the whole page load. It is TRUE now: main.ts's own
    // state.holdIdempotencyKeysByOrderId (a genuine page-lifetime Map,
    // the same shape state.payIdempotencyKeysByTarget already gives Pay)
    // is consulted BEFORE this function is ever called
    // (getOrMintHoldIdempotencyKey), so this throw leaves that in-memory
    // value completely unaffected.
  }
}

/** Removes this order's own persisted hold key for `tenantId`. Never
 * touches any OTHER order's entry. */
export function clearPersistedHoldKey(
  tenantId: string,
  orderId: string,
  storage: KeyValueStorage = window.sessionStorage,
): void {
  try {
    const all = readPersistedHoldKeyMap(storage);
    delete all[storageKey(tenantId, orderId)];
    storage.setItem(HOLD_IDEMPOTENCY_KEYS_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // If we could not persist it in the first place, there is nothing
    // durable left to clear; a no-op here is consistent, not a gap.
  }
}

/** TEN12 (DEC-338): the product name shown where the device does not yet know its customer (the setup screen
 * before a code is redeemed). It is the product, not a shop: every customer's name replaces it once known. */
export const PRODUCT_BRAND_NAME = "Snip & Sip";

/** DEC-348: true once the device knows its customer. Until then the brand headings on the setup and sign-in
 * cards are the product lockup image; after, the customer's name as text beside the mark. */
export function deviceKnowsCustomer(config: TerminalConfig | undefined): boolean {
  const name = config?.businessName?.trim();
  return name !== undefined && name.length > 0;
}

/** The name every title shows: the customer's name as the software owner console lists it, or the product name
 * on a device that is not set up. Never literal shop text. */
export function brandNameFor(config: TerminalConfig | undefined): string {
  const name = config?.businessName?.trim();
  return name === undefined || name.length === 0 ? PRODUCT_BRAND_NAME : name;
}

/** UX96 (DEC-356): the one name the floor header shows beside the home button: the location's name, or the customer's
 * name when the device holds no location name, or nothing. Never both names. */
export function floorLocationNameFor(config: TerminalConfig | undefined): string | undefined {
  const trimmedLocationName = config?.locationName?.trim();
  if (trimmedLocationName !== undefined && trimmedLocationName.length > 0)
    return trimmedLocationName;
  return deviceKnowsCustomer(config) ? brandNameFor(config) : undefined;
}

/** The names the server answers for this device (GET .../device-identity). */
export interface DeviceIdentity {
  businessName: string;
  locationName: string;
  deviceName: string;
}

/** The config with its three display names replaced by the server's current ones. An empty answer keeps what
 * the device had: a blank name is never written over a good one. */
export function applyDeviceIdentity(
  config: TerminalConfig,
  identity: DeviceIdentity,
): TerminalConfig {
  return {
    ...config,
    businessName: identity.businessName.trim() === "" ? config.businessName : identity.businessName,
    locationName: identity.locationName.trim() === "" ? config.locationName : identity.locationName,
    deviceName: identity.deviceName.trim() === "" ? config.deviceName : identity.deviceName,
  } as TerminalConfig;
}
